import React, { useState, useEffect } from 'react';
import { Card, message, Form, Select, Button, Modal, Input, Upload, Popconfirm, Row, Col, Image, Tabs } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UploadOutlined } from '@ant-design/icons';
import ResizableTable from '../components/ResizableTable';
import CampusSelector from '../components/CampusSelector';

const { Option } = Select;
const { TextArea } = Input;

const CurriculumSystem = ({ user }) => {
  const [curriculums, setCurriculums] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);
  const [searchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [uploadedPicUrl, setUploadedPicUrl] = useState('');
  const [ageRangeOptions, setAgeRangeOptions] = useState([]);
  const [promotionUrls, setPromotionUrls] = useState([]);
  const [relateArea, setRelateArea] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const { apiClient } = useAuth();

  const [columns, setColumns] = useState([
    {
      title: '预览照片',
      dataIndex: 'pic',
      key: 'pic',
      width: 120,
      render: (pic) =>
        pic ? <Image src={pic} alt="pic" width={100} height={100} /> : null,
    },
    {
      title: '课程名称',
      dataIndex: 'curriculumName',
      key: 'curriculumName',
      width: 150,
    },
    {
      title: '说明',
      dataIndex: 'curriculumInstructions',
      key: 'curriculumInstructions',
      width: 200,
      ellipsis: true,
    },
    {
      title: '适配年龄段',
      dataIndex: 'ageRange',
      key: 'ageRange',
      width: 120,
    },
    {
      title: '能力',
      dataIndex: 'able',
      key: 'able',
      width: 120,
    },
    {
      title: '单价',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 100,
      render: (price) => price ? `¥${price}` : '',
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
    },
    {
      title: '总价',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => price ? `¥${price}` : '',
    },
    {
      title: '区域价格',
      dataIndex: 'relateArea',
      key: 'relateArea',
      width: 120,
      render: (value) => value ? '区域限制' : '未限制',
    },
    {
      title: '是否公开',
      dataIndex: 'publicKbn',
      key: 'publicKbn',
      width: 100,
      render: (value) => {
        if (String(value) === '1') return '公开';
        if (String(value) === '0') return '不公开';
        return '-';
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <>
          <Button type="link" icon={<EditOutlined />} onClick={() => showEditModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ]);

  // Fetch curriculums
  const fetchCurriculums = async (params = {}) => {
    setLoading(true);
    try {
      // 构建请求参数，仅包含非空值
      const requestParams = {
        pageNo: params.current || 1,
        pageSize: params.pageSize || 10,
      };
      
      // 只有当值存在时才添加到请求参数中
      if (params.curriculumName) {
        requestParams.curriculumName = params.curriculumName;
      }
      if (params.publicKbn !== undefined && params.publicKbn !== null && params.publicKbn !== '') {
        requestParams.publicKbn = params.publicKbn;
      }
      
      const response = await apiClient.post('/sports-server-prod/taller.api.v1/curriculumSystem/pageList', requestParams);

      if (response.data && response.data.code === 200) {
        const list = response.data.data.list || [];
        setCurriculums(list);
        setPagination((prev) => ({
          ...prev,
          current: params.current || 1,
          pageSize: params.pageSize || 10,
          total: response.data.data.totalRecord,
        }));
      } else {
        message.error('获取课程体系列表失败');
        setCurriculums([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    } catch (error) {
      console.error('获取课程体系列表失败:', error);
      message.error('获取课程体系列表失败');
      setCurriculums([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
      }));
    } finally {
      setLoading(false);
    }
  };

  // 获取年龄段选项
  const fetchAgeRangeOptions = async () => {
    try {
      const response = await apiClient.post('/portal-server-prod/sys/enumconfig/v1/list', {
        tableName: 'COURSE_AGE',
        colName: 'COURSE_AGE'
      });
      
      if (response.data && response.data.code === 200) {
        let rawData = response.data.data;
        if (typeof rawData === 'string') {
          rawData = JSON.parse(rawData);
        }
        
        const options = (rawData || []).map((item) => ({
          label: item.enumName,
          value: item.enumCode || item.enumName,
        }));
        
        setAgeRangeOptions(options);
      } else {
        console.error('获取年龄段选项失败:', response.data?.msg || '未知错误');
      }
    } catch (error) {
      console.error('获取年龄段选项异常:', error);
      message.error('获取年龄段选项失败');
    }
  };

  useEffect(() => {
    fetchCurriculums();
    fetchAgeRangeOptions(); // 加载年龄段选项
  }, []);

  // Handle table change
  const handleTableChange = (newPagination) => {
    const tableValues = searchForm.getFieldsValue();
    fetchCurriculums({
      ...tableValues,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  // Search
  const handleSearch = () => {
    const searchValues = searchForm.getFieldsValue();
    fetchCurriculums({
      ...searchValues,
      current: 1,
    });
  };

  // Reset
  const handleReset = () => {
    searchForm.resetFields();
    fetchCurriculums({ current: 1, pageSize: pagination.pageSize });
  };

  // Show edit modal
  const showEditModal = (record = null) => {
    setIsCreating(!record);
    if (record) {
      const { promotion, ...otherRecord } = record;
      editForm.setFieldsValue(otherRecord);
      setUploadedPicUrl(record.pic || '');
      setPromotionUrls(record.promotion ? record.promotion.split(';').filter(Boolean) : []);
      setRelateArea(record.relateArea || '');
    } else {
      editForm.resetFields();
      // Default public
      // 如果有年龄段选项，则默认选择第一个
      const defaultValues = { publicKbn: '1' };
      if (ageRangeOptions && ageRangeOptions.length > 0) {
        defaultValues.ageRange = ageRangeOptions[0].value;
      }
      editForm.setFieldsValue(defaultValues);
      setUploadedPicUrl('');
      setPromotionUrls([]);
      setRelateArea('');
    }
    setEditModalVisible(true);
    setActiveTab('basic'); // 默认打开基础信息标签
  };

  // Save curriculum
  const handleSave = async () => {
    try {
      const values = await editForm.validateFields();
      const userCoach = JSON.parse(localStorage.getItem('user') || '{}').coach;
      const payload = {
        ...values,
        pic: uploadedPicUrl,
        promotion: promotionUrls.join(';'),
        createUser: userCoach,
        updateUser: userCoach,
      };
      await apiClient.post('/sports-server-prod/taller.api.v1/curriculumSystem/add', payload);
      message.success(isCreating ? '添加成功' : '更新成功');
      setEditModalVisible(false);
      fetchCurriculums({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      console.error('保存课程体系失败:', error);
      message.error('操作失败');
    }
  };

  // Delete curriculum
  const handleDelete = async (id) => {
    try {
      await apiClient.post('/sports-server-prod/taller.api.v1/curriculumSystem/del', { id });
      message.success('删除成功');
      fetchCurriculums({
        current: pagination.current,
        pageSize: pagination.pageSize,
      });
    } catch (error) {
      console.error('删除课程体系失败:', error);
      message.error('删除失败');
    }
  };

  // Upload props
  const uploadProps = {
    customRequest: async ({ file, onSuccess, onError }) => {
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await apiClient.post('/sports-server-prod/sys.api.v1/file/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess(response.data);
      } catch (error) {
        onError(error);
      }
    },
    onChange: (info) => {
      if (info.file.status === 'done') {
        message.success('上传成功');
        const data = info.file.response.data;
        const key = Object.keys(data)[0];
        const value = data[key];
        setUploadedPicUrl(value);
      } else if (info.file.status === 'error') {
        message.error('上传失败');
      }
    },
    maxCount: 1,
    showUploadList: false,
  };

  const promotionUploadProps = {
    customRequest: uploadProps.customRequest,
    onChange: (info) => {
      if (info.file.status === 'done') {
        message.success('上传成功');
        const data = info.file.response.data;
        const key = Object.keys(data)[0];
        const value = data[key];
        setPromotionUrls(prev => [...prev, value]);
      } else if (info.file.status === 'error') {
        message.error('上传失败');
      }
    },
    maxCount: 10,
    showUploadList: false,
    accept: 'image/*',
  };

  const tabItems = [
    {
      key: 'basic',
      label: '基础信息',
      children: (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="curriculumName"
                label="课程名称"
                rules={[{ required: true, message: '请输入课程名称' }]}
              >
                <Input maxLength={20} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ageRange"
                label="适配年龄段"
                rules={[{ required: true, message: '请选择适配年龄段' }]}
              >
                <Select placeholder="请选择适配年龄段">
                  {ageRangeOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="able"
                label="能力"
                rules={[{ required: true, message: '请输入能力' }]}
              >
                <Input maxLength={20} showCount />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                  name="unitPrice"
                  label="单价"
                  rules={[{ required: true, message: '请输入单价' }]}
              >
                <Input type="number" step="0.01" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="数量"
                rules={[{ required: true, message: '请输入数量' }]}
              >
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                  name="price"
                  label="总价"
              >
                <Input type="number" step="0.01" readOnly />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="publicKbn"
                label="是否公开"
                rules={[{ required: true, message: '请选择是否公开' }]}
              >
                <Select placeholder="请选择">
                  <Option value="1">公开</Option>
                  <Option value="0">不公开</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="curriculumInstructions"
            label="说明"
            rules={[{ max: 50, message: '说明最多50个字符' }]}
          >
            <TextArea rows={4} maxLength={50} showCount />
          </Form.Item>
          <Form.Item label="预览照片">
            <Upload {...uploadProps} accept="image/*">
              <Button icon={<UploadOutlined />}>上传图片</Button>
            </Upload>
            {uploadedPicUrl && (
              <img
                src={uploadedPicUrl}
                alt="preview"
                style={{ maxWidth: 200, marginTop: 10 }}
              />
            )}
          </Form.Item>
          <Form.Item label="课程详情图片">
            <Upload {...promotionUploadProps}>
              <Button icon={<UploadOutlined />}>上传详情图片</Button>
            </Upload>
            <div style={{ marginTop: 10 }}>
              {promotionUrls.map((url, index) => (
                <div key={url} style={{ position: 'relative', display: 'inline-block', marginRight: 8, marginBottom: 8 }}>
                  <Image
                    src={url}
                    alt={`promotion-${index}`}
                    width={100}
                    height={100}
                  />
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    style={{ position: 'absolute', top: 0, right: 0, color: 'red' }}
                    onClick={() => {
                      const newUrls = promotionUrls.filter((_, i) => i !== index);
                      setPromotionUrls(newUrls);
                    }}
                  />
                </div>
              ))}
            </div>
          </Form.Item>
        </>
      ),
    },
    {
      key: 'area',
      label: '关联区域',
      children: (
        <Form.Item
          name="relateArea"
        >
          <CampusSelector value={relateArea ? relateArea.split(';') : []} onChange={(str) => { setRelateArea(str); editForm.setFieldsValue({ relateArea: str }); }} />
        </Form.Item>
      ),
    },
  ];

  return (
    <Card title="课程体系管理">
      <Form form={searchForm} layout="vertical" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="curriculumName" label="课程名称">
              <Input placeholder="请输入课程名称" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="publicKbn" label="是否公开">
              <Select allowClear placeholder="请选择">
                <Option value="1">公开</Option>
                <Option value="0">不公开</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="ageRange" label="适配年龄段">
              <Select allowClear placeholder="请选择适配年龄段">
                {ageRangeOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={18}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showEditModal()}
            >
              添加课程体系
            </Button>
          </Col>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              搜索
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={handleReset}>
              重置
            </Button>
          </Col>
        </Row>
      </Form>
      <ResizableTable
        columns={columns}
        dataSource={curriculums}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        onColumnsChange={setColumns}
      />
      <Modal
        title={isCreating ? '添加课程体系' : '编辑课程体系'}
        open={editModalVisible}
        onOk={handleSave}
        onCancel={() => setEditModalVisible(false)}
        width={800}
      >
        <Form form={editForm} layout="vertical" onValuesChange={(changedValues, allValues) => {
          if (changedValues.unitPrice !== undefined || changedValues.quantity !== undefined) {
            const unitPrice = allValues.unitPrice;
            const quantity = allValues.quantity;
            if (unitPrice && quantity) {
              const price = unitPrice * quantity;
              editForm.setFieldsValue({ price: price });
            }
          }
        }}>
          <Form.Item name="id" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            items={tabItems}
          />
        </Form>
      </Modal>
    </Card>
  );
};

export default CurriculumSystem;